<template>
  <div class="main print-no-padding">
    <h2 class="is-print-hide title-area">
      <span class="main-title">{{ title }}</span>
    </h2>
    <breadcrumb firstName="药品管理" :second-name="title"></breadcrumb>
    <el-card>
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="left" hide-required-asterisk>
        <basicInfo :basic-info="basicInfo" :drug-usages="drugUsages" :units="units" :mode="mode" @change="change" />
        <moreInfo
          :more-info="moreInfo"
          :usage-frequency-names="usageFrequencyNames"
          :per-units="perUnits"
          :mode="mode"
          @change="change"
        />
        <el-form-item v-if="mode !== 'view'">
          <el-button class="btn" type="primary" @click="submit">提交</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import specificationDetection from '@zyf2e/utils/lib/specificationDetection';
import breadcrumb from '@/components/global/breadcrumb';
import basicInfo from './components/basicInfo';
import moreInfo from './components/moreInfo';
import Fetch from '@/api';

const adaptDrugUsage = drugUsageList => {
  return drugUsageList.reduce((drugUsageObj, drugUsage) => {
    const obj = drugUsage.children.reduce((childrenObj, usage) => {
      childrenObj[usage.value] = drugUsage.value;
      return childrenObj;
    }, {});
    return { ...drugUsageObj, ...obj };
  }, {});
};

export default {
  name: 'editor',
  components: {
    breadcrumb,
    basicInfo,
    moreInfo
  },
  data() {
    return {
      title: '',
      form: {
        // spuId
        id: '',
        // 条形码
        barcode: '',
        // 药品id
        drugId: '',
        // 药品通用名
        name: '',
        // 公司id
        companyId: '',
        // 规格
        spec: '',
        // 生产厂家
        enterprise: '',
        // 国药准字
        approvalNumber: '',
        // 包装单位
        unit: '',
        // 用法
        drugUsage: '',
        // 使用频次
        usageFrequencyName: '',
        // 使用频次编码
        usageFrequencyCode: '',
        // 每次用量
        perNum: '',
        // 用量单位
        perUnit: '',
        // 服药天数
        takingDay: '',
        // 商品编码
        drugCode: '',
        // 药品图片
        thumbnail: '',
        // 药品条形码图片
        barcodePic: '',
        // skuId
        skuId: ''
      },
      rules: {
        barcode: [{ max: 20, message: '条形码最长20个字符', trigger: 'blur' }],
        name: [
          { required: true, message: '请输入药品通用名', trigger: 'change' },
          { max: 50, message: '通用名最长50个字符', trigger: 'change' },
          {
            validator: (rule, value, callback) => {
              if (value && this.form.spec) {
                Fetch.drug_spec_check({ drugName: value, drugSpec: this.form.spec })
                  .then(({ checkResult, checkMessage }) => {
                    if (!checkResult) {
                      this.$alert(
                        [
                          `药品规格填写不规范，错误原因：${checkMessage}`,
                          '请参照示例修改。如有疑问，可联系您的销售经理。'
                        ].join('\n'),
                        '提示',
                        {
                          confirmButtonText: '返回修改',
                          center: true,
                          alignFooter: 'center'
                        }
                      );
                      callback(new Error(checkMessage));
                    } else {
                      callback();
                    }
                  })
                  .catch(() => {
                    callback();
                  });
              }
            },
            trigger: 'blur'
          }
        ],
        spec: [
          { required: true, message: '请输入规格', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (!specificationDetection(value).specificationDetectionSuccess) {
                callback(new Error('规格格式不正确，请重新填写'));
              } else {
                Fetch.drug_spec_check({ drugName: this.form.name, drugSpec: value })
                  .then(({ checkResult, checkMessage }) => {
                    if (!checkResult) {
                      this.$alert(
                        [
                          `药品规格填写不规范，错误原因：${checkMessage}`,
                          '请参照示例修改。如有疑问，可联系您的销售经理。'
                        ].join('\n'),
                        '提示',
                        {
                          confirmButtonText: '返回修改',
                          center: true,
                          alignFooter: 'center'
                        }
                      );
                      callback(new Error(checkMessage));
                    } else {
                      callback();
                    }
                  })
                  .catch(() => {
                    callback();
                  });
              }
            },
            trigger: 'blur'
          }
        ],
        enterprise: [
          { max: 100, message: '生产厂家最长100个字符', trigger: 'blur' },
          { pattern: /^[A-z0-9\u4e00-\u9fa5]*$/, message: '请输入正确的生产厂家', trigger: 'blur' }
        ],
        approvalNumber: [
          { max: 100, message: '国药准字最长100个字符', trigger: 'blur' },
          { pattern: /^[A-z0-9\u4e00-\u9fa5]*$/, message: '请输入正确的国药准字', trigger: 'blur' }
        ],
        unit: [{ required: true, message: '请选择包装单位', trigger: 'change' }],
        perNum: [{ pattern: /^\d*(\.?\d{1,3})$/, message: '请输入正确的每次用量', trigger: 'blur' }],
        takingDay: [
          {
            validator(rule, value, callback) {
              if (value) {
                Fetch.patientApplyMaxDose().then(maxDose => {
                  if (Number.isInteger(Number(value)) && Number(value) > 0 && Number(value) <= maxDose) {
                    callback();
                  } else {
                    callback(new Error(`请输入1-${maxDose}的正整数`));
                  }
                });
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ],
        drugCode: [{ max: 30, message: '商品编码最长30个字符', trigger: 'blur' }]
      },
      drugUsages: [],
      perUnits: [],
      units: [],
      usageFrequencyNames: [],
      mode: '',
      isEdit: false
    };
  },
  computed: {
    basicInfo() {
      const { barcode, name, spec, enterprise, approvalNumber, unit, drugUsage } = this.form;
      return {
        barcode,
        name,
        spec,
        enterprise,
        approvalNumber,
        unit,
        drugUsage
      };
    },
    moreInfo() {
      const { usageFrequencyName, usageFrequencyCode, perNum, perUnit, takingDay, thumbnail, barcodePic, drugCode } =
        this.form;
      return {
        usageFrequencyName,
        usageFrequencyCode,
        perNum,
        perUnit,
        takingDay,
        thumbnail,
        barcodePic,
        drugCode
      };
    }
  },
  created() {
    this.setTitle();
    window.addEventListener('beforeunload', this.refreshHint);

    // 获取药店是否支持商品编码
    this.$store.dispatch('drugsManagement/FetchDrugSetting');
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.refreshHint);
  },
  mounted() {
    this.fetchDrugOption().then(() => {
      this.fetchDrugDetail();
    });
  },
  beforeRouteLeave(to, from, next) {
    if (this.isEdit) {
      setTimeout(() => {
        this.confirmLeave()
          .then(() => {
            return false;
          })
          .catch(() => {
            next();
          });
      });
    } else {
      next();
    }
  },
  methods: {
    setTitle() {
      const { type } = this.$route.params;
      const titleMap = {
        add: '添加药品',
        edit: '编辑药品',
        view: '查看药品'
      };

      this.title = titleMap[type];
      this.mode = type;
    },
    change(key, val) {
      this.isEdit = true;
      this.$set(this.form, key, val);
    },
    async fetchDrugOption() {
      const { drugUsages, perUnits, units, usageFrequencyList } = await Fetch.get_drug_option();
      this.drugUsages = drugUsages.map(o => ({
        label: o.name,
        value: o.name,
        children: o.child.map(c => ({ label: c, value: c }))
      }));
      this.perUnits = perUnits.map(perUnit => ({ label: perUnit, value: perUnit }));
      this.units = units.map(unit => ({ label: unit, value: unit }));
      this.usageFrequencyNames = usageFrequencyList.map(o => ({ label: o.name, value: o.code }));
    },
    async fetchDrugDetail() {
      const { spuId: companySpuId, skuId: companySkuId } = this.$route.params;
      const { drugUsages } = this;

      if (companySpuId) {
        const {
          name,
          drugProductDTOS: [
            {
              id,
              companyId,
              drugId,
              usageFrequencyName,
              usageFrequencyCode,
              spec,
              perNum,
              perUnit,
              unit,
              takingDay,
              drugBarcodeDTOS: [drugBarcode]
            }
          ],
          drugUsage
        } = await Fetch.queryCompanyDrugDetailBySpuId({ companySpuId, companySkuId });
        const drugUsagesMap = adaptDrugUsage(drugUsages);
        const { barcode, thumbnail, barcodePic, enterprise, approvalNumber, id: skuId, drugCode } = drugBarcode || {};
        this.form = {
          id,
          companyId,
          drugId,
          name,
          barcode,
          usageFrequencyName,
          usageFrequencyCode,
          spec,
          perNum,
          perUnit,
          unit,
          drugUsage,
          takingDay,
          enterprise,
          approvalNumber,
          thumbnail,
          barcodePic,
          skuId,
          drugCode
        };
      }
    },
    confirmLeave() {
      return this.$confirm('信息未提交，离开将丢失已有信息', '信息未提交', {
        confirmButtonText: '返回提交',
        cancelButtonText: '仍要放弃',
        customClass: 'confirm',
        distinguishCancelAndClose: true,
        center: true
      });
    },
    refreshHint(e) {
      if (this.isEdit) {
        e.returnValue = '';
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          const {
            id,
            name,
            drugId,
            companyId,
            spec,
            drugUsage,
            perNum,
            perUnit,
            usageFrequencyName,
            usageFrequencyCode,
            unit,
            takingDay,
            barcode,
            enterprise,
            approvalNumber,
            thumbnail,
            barcodePic,
            skuId,
            drugCode
          } = this.form;

          const drugValidRes = await Fetch.get_drug_machine_result({
            name,
            spec,
            usage: drugUsage,
            unit,
            dose: takingDay,
            usageFrequencyName,
            usageFrequencyCode,
            perNum,
            perUnit,
            takingDay
          });
          if (!drugValidRes.passResult) {
            this.$alert(`药品信息填写不规范，错误信息：${drugValidRes.message || ''}。`, '提示', {
              confirmButtonText: '返回修改',
              center: true,
              alignFooter: 'center'
            });
            return;
          }

          return Fetch.addOrUpdateCompanyDrug({
            name,
            drugUsage: drugUsage,
            drugProductDTOS: [
              {
                companyId: companyId || null,
                id: id || null,
                drugId: drugId || null,
                spec: spec || null,
                perNum: perNum || null,
                perUnit: perUnit || null,
                usageFrequencyName: usageFrequencyName || null,
                usageFrequencyCode: usageFrequencyCode || null,
                unit: unit || null,
                takingDay: takingDay || null,
                drugBarcodeDTOS: [
                  {
                    barcode: barcode || null,
                    enterprise: enterprise || null,
                    approvalNumber: approvalNumber || null,
                    thumbnail: thumbnail || null,
                    barcodePic: barcodePic || null,
                    id: skuId || null,
                    drugCode: drugCode || null
                  }
                ]
              }
            ]
          }).then(async () => {
            await this.$alert('提交成功', '提示', {
              confirmButtonText: '我知道了',
              center: true,
              alignFooter: 'center'
            });

            this.isEdit = false;
            this.$router.push('/drugsManagement');
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.btn {
  width: 174px;
}
</style>
